/** @module Scan screen panels: status chips, session summary and the diagnostics readout. */
import { useTranslation } from "react-i18next";

import type { AutoCaptureState } from "@/features/scan/use-auto-capture";
import type { ScanEntry } from "@/features/scan/use-scan-session";

/** What the last photo was like; shown in the diagnostics and sent to the server log. */
export interface ShotInfo {
  trigger: "auto" | "manual";
  photo: string;
  kilobytes: number;
  /** Time from something appearing in the frame to the shot; null for a manual shot. */
  settleMs: number | null;
  at: number;
}

/** What the run produced; a deleted photo has already left the list. */
export function counts(entries: ScanEntry[]) {
  const done = (decision: string) =>
    entries.filter(
      (entry) => !entry.booked && entry.state === "done" && entry.decision === decision,
    ).length;
  return {
    total: entries.length,
    uploaded: entries.filter((entry) => entry.scanId !== null).length,
    finished: entries.filter((entry) => entry.state === "done").length,
    booked: entries.filter((entry) => entry.booked).length + done("auto"),
    review: done("review"),
    noMatch: done("no_match"),
    failed: entries.filter((entry) => entry.state === "failed").length,
  };
}

function round(value: number): string {
  return value.toFixed(1);
}

/** Everything the detector and the last photo measured; for tuning and for bug reports. */
export function DiagnosticsPanel({
  detection,
  lastShot,
  settings,
}: {
  detection: AutoCaptureState;
  lastShot: ShotInfo | null;
  settings: { delay: number; crop: boolean; zoom: number; camera: string };
}) {
  const { t } = useTranslation();
  const rows: [string, string][] = [
    [t("scan.diag.video"), detection.video || "–"],
    [t("scan.diag.fps"), detection.fps === null ? "–" : String(detection.fps)],
    [t("scan.diag.state"), `${detection.readiness} · ${Math.round(detection.progress * 100)}%`],
    [t("scan.diag.novelty"), detection.surface ? round(detection.novelty) : "–"],
    [t("scan.diag.change"), round(detection.change)],
    [
      t("scan.diag.lastPhoto"),
      lastShot ? `${lastShot.photo} · ${lastShot.kilobytes} KB · ${lastShot.trigger}` : "–",
    ],
    [
      t("scan.diag.settle"),
      lastShot?.settleMs === null || !lastShot ? "–" : `${lastShot.settleMs} ms`,
    ],
    [
      t("scan.diag.settings"),
      `${settings.delay} s · ${settings.crop ? t("scan.diag.cropOn") : t("scan.diag.cropOff")} · ${settings.zoom.toFixed(1)}× · ${settings.camera}`,
    ],
  ];
  return (
    <section className="flex flex-col gap-2 rounded-[var(--radius-sm)] bg-(--surface-2) px-3 py-2 font-(family-name:--font-mono) text-[length:var(--text-xs)]">
      {/* The end-to-end tests read this first line; keep its wording. */}
      <p className="tabular">
        {t("scan.motion")} {round(detection.motion)} / {round(detection.baseline)} ·{" "}
        {t("scan.detail")} {round(detection.detail)} · {t("scan.contrast")}{" "}
        {round(detection.contrast)} · {detection.readiness} · {Math.round(detection.progress * 100)}
        %
      </p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-(--text-muted)">{label}</dt>
            <dd className="tabular">{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
